import React, { useMemo } from "react";

import classNames from "classnames";
import { Formik } from "formik";
import { FormattedMessage, useIntl } from "gatsby-plugin-react-intl";
import PropTypes from "prop-types";
import Col from "react-bootstrap/Col";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import { useDispatch, useSelector } from "react-redux";
import { mixed, object, string } from "yup";

import { setRegistrationData } from "../../../features/registrationSlice";
import { ReactComponent as IconError18 } from "../../../icons/icon/18/a-icon-error-18.svg";
import { ReactComponent as IconFurther18 } from "../../../icons/icon/18/a-icon-further-18.svg";
import { ReactComponent as IconFurther24 } from "../../../icons/icon/24/a-icon-further-24.svg";
import DataLayer from "../../../utils/dataLayer";
import ScrollToFieldError from "../../../utils/formik/scrollToFieldError";
import AdaptiveIcon from "../../adaptiveIcon/adaptiveIcon";
import IconButton from "../../button/iconButton";
import CleaveFormField from "../../dynamicForm/cleaveFormField/cleaveFormField";
import FormField from "../../dynamicForm/formField/formField";
import RegistrationDataHelper from "../registrationDataHelper";
import RegistrationDialogHeadline from "../registrationDialogHeadline";

const RegistrationDialogStepRegistrationPersonalInformationCl = ({ next }) => {
  const registrationData = useSelector((state) => state.registration.registrationData);
  const dispatch = useDispatch();

  const intl = useIntl();

  const requiredMessage = intl.formatMessage({ id: "form.required" });
  const onlyAlphabeticMessage = intl.formatMessage({ id: "form.error.only_alphabetic_chars" });

  const validationSchema = useMemo(() => object().shape({
    firstname: string()
      .min(2, requiredMessage)
      .required(requiredMessage)
      .max(40, intl.formatMessage(
        { id: "form.error.max_length_exact" },
        {
          value: 40,
        },
      ))
      .trim()
      .matches(/^[^\d]+$/, onlyAlphabeticMessage)
      .matches(RegistrationDataHelper.noneEmojiRegex, onlyAlphabeticMessage),
    gender: mixed()
      .required(requiredMessage)
      .oneOf(["M", "F"], requiredMessage),
    lastname: string()
      .min(2, requiredMessage)
      .required(requiredMessage)
      .max(100, intl.formatMessage(
        { id: "form.error.max_length_exact" },
        {
          value: 100,
        },
      ))
      .trim()
      .matches(/^[^\d]+$/, onlyAlphabeticMessage)
      .matches(RegistrationDataHelper.noneEmojiRegex, onlyAlphabeticMessage),
    rutRun: string()
      .min(2, requiredMessage)
      .required(requiredMessage)
      .max(10, intl.formatMessage(
        { id: "form.error.max_length_exact" },
        {
          value: 10,
        },
      ))
      .matches(/^([0-9]{8})-([\dkK])$/, intl.formatMessage({ id: "form.error.invalid_rut_run_pattern" })),
  }), []);

  return (
    <>
      <RegistrationDialogHeadline
        headline="registration.personal.title"
      />
      <Formik
        initialValues={registrationData}
        validationSchema={validationSchema}
        validateOnMount
        validate={(values) => {
          const errors = {};

          const {
            rutRun,
          } = values;

          if (rutRun && rutRun.length === 9) {
            errors.rutRun = intl.formatMessage({ id: "form.error.rut_run_pattern_eight_digits" });
          }

          if (rutRun && rutRun.length === 10) {
            const tmp = rutRun.split("-");
            let digv = tmp[1];
            let rut = tmp[0];

            if (digv === "K") {
              digv = "k";
            }

            let m = 0;
            let s = 1;

            for (; rut; rut = Math.floor(rut / 10)) {
              s = (s + (rut % 10) * (9 - ((m % 6)))) % 11;
              m += 1;
            }

            const result = s ? (s - 1).toString() : "k";

            if (result !== digv) {
              errors.rutRun = intl.formatMessage({ id: "form.error.invalid_rut_run" });
            }
          }

          return errors;
        }}
        onSubmit={(values, formikBag) => {
          formikBag.setSubmitting(true);

          dispatch(
            setRegistrationData({
              firstname: values.firstname,
              gender: values.gender,
              lastname: values.lastname,
              rutRun: values.rutRun,
            }),
          );

          DataLayer.pushEvent("pp_registration_step_personal_information", { event_value: "success" });
          DataLayer.pushEvent("pp_registration_step_personal_information_value",
            {
              dimension_reg_sex: values.gender,
              event_value: values.gender,
            });

          next();
        }}
      >
        {({
          values,
          errors,
          touched,
          isSubmitting,
          handleChange,
          handleBlur,
          handleSubmit,
          setFieldTouched,
        }) => (
          <Form onSubmit={handleSubmit}>
            <Row>
              <Col xs={12}>
                <div className="required-sign typo-label">
                  <FormattedMessage id="registration.personal.gender.title" />
                </div>
                <Form.Group>
                  <fieldset aria-errormessage="gender-error" aria-invalid={errors.gender && touched.gender}>
                    <Form.Check
                      type="radio"
                      label={intl.formatMessage({ id: "form.sex.male" })}
                      name="gender"
                      value="M"
                      id="gender-male"
                      checked={values.gender === "M"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inline
                      className={classNames("mb-0", errors.gender && touched.gender ? "is-invalid" : "")}
                    />
                    <Form.Check
                      type="radio"
                      label={intl.formatMessage({ id: "form.sex.female" })}
                      name="gender"
                      value="F"
                      id="gender-female"
                      checked={values.gender === "F"}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      inline
                      className={classNames("mb-0", errors.gender && touched.gender ? "is-invalid" : "")}
                    />
                  </fieldset>
                  {errors.gender && touched.gender && (
                    <Form.Text className="invalid-feedback" id="gender-error">
                      <IconError18 />{errors.gender}
                    </Form.Text>
                  )}
                </Form.Group>
              </Col>

              <Col xs={12} lg={6}>
                <FormField
                  onChange={(event) => {
                    setFieldTouched("gender", true);
                    handleChange(event);
                  }}
                  onBlur={(event) => {
                    setFieldTouched("gender", true);
                    handleBlur(event);
                  }}
                  id="firstname"
                  label={intl.formatMessage({ id: "registration.personal.firstname" })}
                  type="text"
                  maxLength={40}
                  touched={touched}
                  errors={errors}
                  values={values}
                  required
                />
              </Col>

              <Col xs={12} lg={6}>
                <FormField
                  onChange={handleChange}
                  onBlur={handleBlur}
                  id="lastname"
                  label={intl.formatMessage({ id: "registration.personal.lastname" })}
                  type="text"
                  maxLength={100}
                  touched={touched}
                  errors={errors}
                  values={values}
                  required
                />
              </Col>

              <Col xs={12}>
                <CleaveFormField
                  label={intl.formatMessage({ id: "registration.personal.rut_run" })}
                  values={values}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  touched={touched}
                  id="rutRun"
                  errors={errors}
                  options={{
                    blocks: [8, 1],
                    delimiter: "-",
                    numericOnly: false,
                  }}
                  maxLength={10}
                  required
                  inputMode="text"
                />
              </Col>

              <ScrollToFieldError />

              <Col xs={12}>
                <Form.Group>
                  <IconButton type="submit" disabled={isSubmitting}>
                    <FormattedMessage id="registration.personal.button_next" />
                    <AdaptiveIcon
                      sm={<IconFurther18 />}
                      lg={<IconFurther24 />}
                    />
                  </IconButton>
                </Form.Group>

                <div className="footnote--required">
                  <span className="required-sign" />
                  <FormattedMessage id="registration.required_fields" />
                </div>
              </Col>
            </Row>
          </Form>
        )}
      </Formik>
    </>
  );
};

RegistrationDialogStepRegistrationPersonalInformationCl.stepName = "registration_personal_information";

RegistrationDialogStepRegistrationPersonalInformationCl.propTypes = {
  next: PropTypes.func.isRequired,
};

export default RegistrationDialogStepRegistrationPersonalInformationCl;
